import { KeyboardEvent, useEffect, useState } from "react";

import { Search, X } from "lucide-react";

import { IgTextField, IgTooltip } from "@intelligems/ig-theme";
import { CircularProgress, IconButton, Stack, useTheme } from "@mui/material";
import { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";

import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction,
  ExperienceBuilderSelector,
} from "@src/store/slices/experienceBuilder";
import { getOrganization } from "@src/store/slices/organization";

import { useNavigatorContext } from "../context/useNavigatorContext";
import { validateNavigableUrl } from "../context/validateNavigableUrl";

interface NavigatorInputProps {
  params: AutocompleteRenderInputParams;
}

export const NavigatorInput = ({ params }: NavigatorInputProps) => {
  const dispatch = useDispatch();
  const { inputValue, setInputValue, isLoading } = useNavigatorContext();
  const location = useSelector(ExperienceBuilderSelector.getDisplayLocation);
  const org = useSelector(getOrganization);
  const configuredDomains = [
    org?.domain,
    ...(org?.secondaryDomains ?? []),
  ].filter((domain): domain is string => Boolean(domain));
  const theme = useTheme();
  const errorColor = theme.igBackgroundColors.interactive.error.default;
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear a stale URL error as soon as the merchant edits the input.
  useEffect(() => setError(null), [inputValue]);

  // Auto-dismiss the error tooltip a few seconds after it appears, so a bad URL
  // doesn't leave the tooltip pinned open until the next edit.
  useEffect(() => {
    if (error === null) {
      return;
    }
    const id = setTimeout(() => setError(null), 3000);
    return () => clearTimeout(id);
  }, [error]);

  const pathLabel = location === "/" ? "Home" : location;
  const placeholder = focused ? "Search pages" : pathLabel;
  const tooltipTitle = inputValue ? "" : pathLabel;

  // Enter on a typed http(s) URL navigates the preview there (or shows an inline
  // error if it's malformed or off the configured domains). Input that isn't a
  // URL attempt yields `undefined` — Enter is left to the Autocomplete's normal
  // handling. Stop propagation on a real attempt so MUI's root keydown handler
  // doesn't also treat Enter as "select the highlighted option".
  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key !== "Enter") {
      return;
    }

    const result = validateNavigableUrl(inputValue, configuredDomains);
    if (!result) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (result.type === "error") {
      setError(result.message);
      return;
    }

    void dispatch(ExperienceBuilderAction.setTargetUrl(result.url));
    setInputValue("");
    (event.target as HTMLElement).blur();
  };

  return (
    <IgTooltip
      title={error ?? tooltipTitle}
      // Always controlled: an error forces it open; otherwise it follows hover
      // like a normal tooltip. Never let `open` flip between undefined and a
      // boolean — MUI warns on switching uncontrolled<->controlled.
      open={error !== null || hovered}
      onOpen={() => setHovered(true)}
      onClose={() => setHovered(false)}
      slotProps={
        error !== null
          ? {
              tooltip: {
                sx: { backgroundColor: `${errorColor} !important` },
              },
              arrow: { sx: { color: `${errorColor} !important` } },
            }
          : undefined
      }
    >
      <IgTextField
        {...params}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        sx={{
          "& input::placeholder": {
            color: focused ? "text.placeholder" : "text.primary",
            opacity: 1,
          },
        }}
        slotProps={{
          input: {
            ...params.InputProps,
            onKeyDown: handleKeyDown,
            startAdornment: (
              <Search
                size={14}
                color={theme.palette.text.placeholder}
                style={{ marginLeft: 4, flexShrink: 0 }}
              />
            ),
            endAdornment: (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {isLoading && (
                  <CircularProgress
                    size={16}
                    sx={{ display: "block", mr: 0.75 }}
                  />
                )}
                {inputValue && (
                  <IconButton
                    size="small"
                    onClick={() => setInputValue("")}
                    sx={{ p: 0.25 }}
                  >
                    <X size={14} />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </Stack>
            ),
          },
        }}
      />
    </IgTooltip>
  );
};
